import { z } from "zod";
import { looseUuid, NhsNumberSchema } from "@thalamos/common";
export const LcrPatientSchema = z.object({
    /**
     * The UUID for the patient in Aspire.
     */
    id: looseUuid(),
    nhsNumber: NhsNumberSchema.nullable(),
    name: z
        .object({
        given: z.string().min(1).max(255),
        family: z.string().min(1).max(255),
    })
        .optional(),
    dateOfBirth: z.iso.date().nullable().optional(),
    deceasedDateTime: z.iso.datetime().nullable().optional(),
    address: z
        .object({
        address: z.string().max(255),
        postalCode: z.string().max(20),
    })
        .optional(),
});
export const LcrShadowStateBaseSchema = z.object({
    patientId: LcrPatientSchema.shape.id,
    entityType: z.string().min(1).max(255),
    entityTypeVersion: z.string().min(1).max(255),
    entityId: looseUuid(),
    entityData: z.record(z.string(), z.unknown()).nullable(),
});
///
/**
 * This is a versioned schema capturing the base fields we believe are
 * stored in the London Care Record about Patient Demographics.
 * We store this data in Aspire as a "shadow" of what is in the LCR,
 * because we cannot read the content from there.
 * The actual data in the LCR may be derived from this.
 *
 * If the field definitions change, then a new versioned schema should
 * be created and the version field updated.
 */
export const LcrShadowStatePatientV1Schema = LcrShadowStateBaseSchema.extend({
    patientId: LcrPatientSchema.shape.id,
    entityType: z.literal("patient"),
    entityTypeVersion: z.literal("1"),
    entityId: LcrPatientSchema.shape.id,
    entityData: LcrPatientSchema.pick({
        nhsNumber: true,
        name: true,
        dateOfBirth: true,
        deceasedDateTime: true,
        address: true,
    }).nullable(),
}).brand();
/**
 * When a patient is merged on eMHA, we tell the LCR it is merged, and
 * store the details of the patient it was merged into (the Primary Patient)
 * here. That is because the LCR merge message is in the context of the
 * primary patient, and we need to provide all of those demographics again.
 */
export const LcrShadowStateMergedPatientV1Schema = LcrShadowStateBaseSchema.extend({
    patientId: LcrPatientSchema.shape.id,
    entityType: z.literal("mergedPatient"),
    entityTypeVersion: z.literal("1"),
    entityId: LcrPatientSchema.shape.id,
    entityData: z.object({
        primaryPatient: LcrPatientSchema.pick({
            id: true,
            nhsNumber: true,
            name: true,
            dateOfBirth: true,
            deceasedDateTime: true,
            address: true,
        }),
    }),
}).brand();
/**
 * This is the data required to send to the LCR to register a patient.
 */
export const LcrMessagePatientRegistrationSchema = z
    .object({
    type: z.literal("patientRegistration"),
    patient: LcrPatientSchema.pick({
        id: true,
        nhsNumber: true,
        name: true,
        dateOfBirth: true,
        deceasedDateTime: true,
        address: true,
    }),
})
    .brand();
/**
 * This is the data required to send to the LCR to register a patient.
 */
export const LcrMessagePatientMergeSchema = z
    .object({
    type: z.literal("patientMerge"),
    patient: LcrPatientSchema.pick({
        id: true,
        nhsNumber: true,
        name: true,
        dateOfBirth: true,
        deceasedDateTime: true,
        address: true,
    }),
    secondaryPatientId: z.string().min(1).max(255),
})
    .brand();
export const LcrAlertSchema = z.object({
    id: z.string().endsWith("-alert"),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    alertDescription: z.string().min(1).max(1000),
    alertText: z.string().min(1).max(1000),
    identifier: z.enum([
        "Section 2",
        "Section 3",
        "CTO",
        "Section 4",
        "Section 5(2)",
        "Section 5(4)",
        "Section 136",
    ]),
    status: z.enum(["ACTIVE", "INACTIVE"]),
});
/**
 * This is a versioned schema capturing the base fields we believe are
 * stored in the London Care Record about an Alert.
 * We store this data in Aspire as a "shadow" of what is in the LCR,
 * because we cannot read the content from there.
 * The actual data in the LCR may be derived from this.
 *
 * If the field definitions change, then a new versioned schema should
 * be created and the version field updated.
 */
export const LcrShadowStateAlertV1Schema = LcrShadowStateBaseSchema.extend({
    patientId: LcrPatientSchema.shape.id,
    entityType: z.literal("alert"),
    entityTypeVersion: z.literal("1"),
    entityId: z.string(),
    entityData: LcrAlertSchema.pick({
        alertDescription: true,
        alertText: true,
        identifier: true,
        status: true,
    })
        .extend({
        assigningAuthorityOid: z.string().min(1).max(255),
        startDateTime: z.iso.datetime(),
        endDateTime: z.iso.datetime(),
    })
        .nullable(),
}).brand();
export const LcrMessageAlertSchema = z
    .object({
    type: z.literal("alert"),
    assigningAuthority: z.string().min(1).max(255),
    patient: LcrPatientSchema.pick({
        id: true,
        nhsNumber: true,
        name: true,
        dateOfBirth: true,
        deceasedDateTime: true,
        address: true,
    }),
    alert: LcrAlertSchema.pick({
        id: true,
        startDate: true,
        endDate: true,
        alertDescription: true,
        alertText: true,
        identifier: true,
    }),
    action: z.enum(["ACTIVE", "INACTIVE", "IN ERROR"]),
})
    .brand();
export const LcrReportSchema = z.object({
    id: z.string().endsWith("-report"),
    documentDateTime: z.iso.datetime(),
    base64Data: z.union([
        z.base64(), // Base-64 encoded PDF
        z.literal("IN ERROR"), // Used as a placeholder when hard-deleting a report
    ]),
});
/**
 * This is a versioned schema capturing the base fields we believe are
 * stored in the London Care Record about a Report.
 * We store this data in Aspire as a "shadow" of what is in the LCR,
 * because we cannot read the content from there.
 * The actual data in the LCR may be derived from this.
 *
 * If the field definitions change, then a new versioned schema should
 * be created and the version field updated.
 */
export const LcrShadowStateReportV1Schema = LcrShadowStateBaseSchema.extend({
    patientId: LcrPatientSchema.shape.id,
    entityType: z.literal("report"),
    entityTypeVersion: z.literal("1"),
    entityId: z.string(),
    // The following fields are not stored in the LCR directly, but
    // encoded into the PDF which is encoded in base64Data.
    // We store them here so we can regenerate the PDF if needed.
    entityData: z
        .object({
        assigningAuthorityOid: z.string().min(1).max(255),
        pdfTemplateVersion: z.string().min(1),
        patient: LcrPatientSchema.pick({
            nhsNumber: true,
            name: true,
            dateOfBirth: true,
        }),
        mhaStatus: z.object({
            identifier: LcrAlertSchema.shape.identifier,
            startDateTime: LcrShadowStateAlertV1Schema.shape.entityData.unwrap().shape
                .startDateTime,
            endDateTime: LcrShadowStateAlertV1Schema.shape.entityData.unwrap().shape
                .endDateTime,
            detainingOrganisations: z.array(z.object({
                hospital: z.object({ name: z.string().min(1) }),
                trust: z.object({ name: z.string().min(1) }).optional(),
            })),
            // Section 136 only: the single police force that exercised the power.
            // Held at the status level (not per detaining organisation) because one
            // force can span multiple Places of Safety.
            policeForce: z.object({ name: z.string().min(1) }).optional(),
            completionStatus: z.enum([
                "active", // this detention status is still in force
                "expired", // detention status automatically expired (shouldn't happen but sometimes does due to late forms)
                "not-detained", // patient has been explicitly marked as discharged from MHA (manually or by completion of a form)
                "transferred-out-of-scope", // patient has an MHA status which is outside the scope of the LCR
                "unknown-incomplete", // patient has an incomplete MHA status and we don't know if they are still detained
                "completed", // patient has moved to another section but is still detained
            ]),
        }),
    })
        .nullable(),
}).brand();
export const LcrMessageReportSchema = z
    .object({
    type: z.literal("report"),
    assigningAuthority: z.string().min(1).max(255),
    patient: LcrPatientSchema,
    report: LcrReportSchema,
    action: z.enum(["F", "IN ERROR"]),
})
    .brand();
export const LcrShadowStateSchema = z.union([
    LcrShadowStatePatientV1Schema,
    LcrShadowStateMergedPatientV1Schema,
    LcrShadowStateAlertV1Schema,
    LcrShadowStateReportV1Schema,
]);
export const LcrMessageSchema = z.discriminatedUnion("type", [
    LcrMessagePatientRegistrationSchema,
    LcrMessageAlertSchema,
    LcrMessageReportSchema,
    LcrMessagePatientMergeSchema,
]);
//# sourceMappingURL=londonCareRecord.js.map